import type {DiffLine, Thread, ThreadsData} from '@github-ui/diff-lines'
import type {DiffEntry, DiffSide} from '@github-ui/diff-lines/types'
import type {PullRequestFileTreeDiff, ThreadMarker} from '../page-data/payloads/file-tree'
import type {Markers} from '../stores/slices/markers'
import type {
  DiffAnnotation,
  Thread as ConversationThread,
  ThreadSubject,
} from '@github-ui/diff-lines/conversation-types'
import type {PendingReview} from '../stores/slices/pending-review'

type AnnotationsData = {
  totalCount: number
  annotations: Array<DiffAnnotation | null> | null
}

export function findMarkersByPosition(
  diffLine: DiffLine,
  diffEntryData: PullRequestFileTreeDiff,
  markersData: Markers,
): {threadsData: ThreadsData; annotationsData: AnnotationsData} | undefined {
  let diffLineKey = diffLine.type !== 'DELETION' ? `R${diffLine.right}` : `L${diffLine.left}`

  if (!diffEntryData || !diffEntryData.markersMap) {
    return undefined
  }

  let markerPositions = diffEntryData.markersMap[diffLineKey]
  if (diffLine.type === 'CONTEXT' || diffLine.type === 'INJECTED_CONTEXT') {
    if (markerPositions === undefined) {
      markerPositions = {threads: [], annotations: []}
    }
    // if we have a context or injected context line, we need to check both sides of that line for markers
    // in the markers map and merge them, because depending on which side the context comment was made would determine
    // which diff line key was used
    diffLineKey = `L${diffLine.left}`
    const leftSideMarkerPositions = diffEntryData.markersMap[diffLineKey]
    markerPositions = {
      threads: markerPositions.threads.concat(leftSideMarkerPositions?.threads || []),
      annotations: markerPositions.annotations,
    }
  }
  const threads: Thread[] = []
  const annotations: DiffAnnotation[] = []
  let totalCommentsCount = 0

  if (markerPositions) {
    for (const marker of markerPositions.threads) {
      if (marker.id !== undefined) {
        const thread = markersData.threads[Number(marker.id)]
        if (thread) {
          let startSide: DiffSide | undefined = undefined
          let startLineNumber = undefined
          if (marker.start) {
            startSide = marker.start[0] === 'L' ? 'LEFT' : 'RIGHT'
            startLineNumber = parseInt(marker.start.slice(1)) || -1
          }

          totalCommentsCount += thread.commentsData.comments.length
          threads.push({
            id: thread.id,
            isOutdated: thread.isOutdated || false,
            commentsData: {
              __id: thread.commentsData.__id,
              comments: thread.commentsData.comments.map(comment => ({
                id: comment.databaseId ?? undefined,
                author: comment.author ?? null,
              })),
              totalCount: thread.commentsData.comments.length,
            },
            diffSide: diffLine.type === 'DELETION' ? 'LEFT' : 'RIGHT',
            line: diffLine.type === 'DELETION' ? diffLine.left : diffLine.right,
            startDiffSide: startSide,
            startLine: startLineNumber,
          })
        }
      }
    }

    for (const marker of markerPositions.annotations) {
      if (marker.id !== undefined) {
        const annotation = markersData.annotations[Number(marker.id)]
        if (annotation) {
          annotations.push(annotation)
        }
      }
    }
  }

  return {
    threadsData: {
      threads,
      totalCommentsCount,
      totalCount: threads.length,
    },
    annotationsData: {
      totalCount: annotations.length,
      annotations,
    },
  }
}

export function extractAnnotationMarkerId(
  parsedAnnotationId: number,
  annotations: Record<number, Pick<DiffAnnotation, 'id' | 'databaseId'>> | undefined,
) {
  if (!parsedAnnotationId || !annotations) {
    return undefined
  }

  for (const annotation of Object.values(annotations)) {
    if (annotation && annotation.databaseId === parsedAnnotationId) {
      return annotation.id
    }
  }

  return undefined
}

// Narrowed type for threads to match the expected structure in extractCommentThreadMarkerId
// This simplifies the type checking for tests and ensures we only work with the necessary properties
type ThreadForHashFunctions = {
  id: ConversationThread['id']
  commentsData: {
    comments: Array<Pick<ConversationThread['commentsData']['comments'][number], 'databaseId'>>
  }
}

export function extractCommentThreadMarkerId(
  parsedCommentId: number | undefined,
  threads: Record<number, ThreadForHashFunctions> | undefined,
) {
  if (!parsedCommentId || !threads) {
    return undefined
  }

  for (const thread of Object.values(threads)) {
    if (thread) {
      for (const comment of thread.commentsData.comments) {
        if (comment.databaseId === parsedCommentId) {
          return thread.id
        }
      }
    }
  }

  return undefined
}

export function buildDiffSummaryMap(diffSummaries: PullRequestFileTreeDiff[]): Record<string, PullRequestFileTreeDiff> {
  return diffSummaries.reduce<Record<string, PullRequestFileTreeDiff>>((buildingDiffSummaryMap, diffSummary) => {
    buildingDiffSummaryMap[diffSummary.path] = diffSummary
    return buildingDiffSummaryMap
  }, {})
}

export function buildCollapsedDiffStatusMap(diffSummaries: PullRequestFileTreeDiff[]): Record<string, boolean> {
  return diffSummaries.reduce<Record<string, boolean>>((buildingCollapsedDiffStatusMap, diffSummary) => {
    buildingCollapsedDiffStatusMap[diffSummary.path] = !!diffSummary.markedAsViewed
    return buildingCollapsedDiffStatusMap
  }, {})
}

export function buildPendingReview(pendingReviewData: PendingReview) {
  const pendingThreadIDs = pendingReviewData.comments.map(comment => parseInt(comment.threadId))
  return {
    id: pendingReviewData.id,
    pendingReviewIDs: pendingThreadIDs,
    comments: pendingReviewData.comments,
  }
}

export function buildDiffEntryMap(diffEntries: DiffEntry[]): Record<string, DiffEntry> {
  return diffEntries.reduce<Record<string, DiffEntry>>((buildingDiffEntryMap, diffEntry) => {
    buildingDiffEntryMap[diffEntry.path] = diffEntry
    return buildingDiffEntryMap
  }, {})
}

export function buildNormalizedMarkers(
  markers: Markers | undefined,
  diffSummaries: PullRequestFileTreeDiff[],
): Markers | undefined {
  if (!markers) {
    return undefined
  }

  const markersWithSubjects: Markers = {threads: {}, annotations: {}}
  if (!diffSummaries) return markersWithSubjects

  for (const diffSummary of diffSummaries) {
    if (!diffSummary.markersMap || Object.keys(diffSummary.markersMap).length === 0) continue
    for (const markerKey in diffSummary.markersMap) {
      const diffSummaryMarker = diffSummary.markersMap[markerKey]
      if (!diffSummaryMarker?.threads) continue
      for (const threadMarker of diffSummaryMarker.threads) {
        const subject = getSubjectValuesFromThreadMarkers(markerKey, threadMarker)
        const thread = markers.threads[Number(threadMarker.id)]
        if (thread && subject) {
          // TODO: This is a workaround to avoid the ESLint error about mutating props directly.
          // Ideally, we should refactor this to avoid direct mutation, but since we need to do a quick fix for performance, this mimics the previous behavior.

          thread.subject = subject
        }
        if (thread) {
          //totalCommentCountForSummary += thread.commentsData.comments.length
          markersWithSubjects.threads[Number(threadMarker.id)] = thread
        }
      }
    }
  }

  return markersWithSubjects
}

const diffSideMap = {
  L: 'LEFT',
  R: 'RIGHT',
} as const

type LineSideLetter = 'L' | 'R'

function getSubjectValuesFromThreadMarkers(
  diffSummaryKey: string,
  threadMarker: ThreadMarker,
): ThreadSubject | undefined {
  if (diffSummaryKey === 'FILE') {
    return undefined
  }

  const endDiffSideChar = diffSummaryKey[0]
  if (!isLineSideLetter(endDiffSideChar)) {
    return undefined
  }
  const endDiffSideLetter = endDiffSideChar
  const endLineNumber = Number(diffSummaryKey.slice(1))

  let startLineNumber = -1
  let startDiffSideLetter: LineSideLetter = 'R'

  if (threadMarker.start) {
    startLineNumber = parseInt(threadMarker.start.slice(1))
    const startChar = threadMarker.start[0]
    if (isLineSideLetter(startChar)) {
      startDiffSideLetter = startChar
    }
  } else {
    startLineNumber = endLineNumber
    startDiffSideLetter = endDiffSideLetter
  }

  const endDiffSide = diffSideMap[endDiffSideLetter]
  const startDiffSide = diffSideMap[startDiffSideLetter]

  return {
    endLine: endLineNumber,
    endDiffSide,
    startLine: startLineNumber,
    startDiffSide,
  }
}
function isLineSideLetter(value: string | undefined): value is LineSideLetter {
  return value === 'L' || value === 'R'
}
